import { useState, useEffect } from 'react';

// Separate stages: 0%, 25%, 50%, 75%, 90%, 100%
const PROGRESS_STAGES = [
  { value: 0, label: '0%', color: '#9CA3AF', bgColor: 'bg-gray-400' },
  { value: 25, label: '25%', color: '#EF4444', bgColor: 'bg-red-500' },
  { value: 50, label: '50%', color: '#84CC16', bgColor: 'bg-lime-500' },
  { value: 75, label: '75%', color: '#22C55E', bgColor: 'bg-green-500' },
  { value: 90, label: '90%', color: '#3B82F6', bgColor: 'bg-blue-500' },
  { value: 100, label: '100%', color: '#8B5CF6', bgColor: 'bg-purple-500' },
];

const getProgressColor = (progress) => {
  if (progress === 0) return '#9CA3AF';
  if (progress <= 25) return '#EF4444';
  if (progress <= 50) return '#84CC16';
  if (progress <= 75) return '#22C55E';
  if (progress <= 90) return '#3B82F6';
  return '#8B5CF6';
};

const getProgressBgClass = (progress) => {
  if (progress === 0) return 'bg-gray-400';
  if (progress <= 25) return 'bg-red-500';
  if (progress <= 50) return 'bg-lime-500';
  if (progress <= 75) return 'bg-green-500';
  if (progress <= 90) return 'bg-blue-500';
  return 'bg-purple-500';
};

const snapToStage = (value) => {
  const stages = [0, 25, 50, 75, 90, 100];
  return stages.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
};

export default function ProgressSlider({
  progress = 0,
  onProgressChange,
  disabled = false,
  showLabels = true,
  size = 'md'
}) {
  const [localVal, setLocalVal] = useState(progress);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) setLocalVal(progress);
  }, [progress, dragging]);

  const displayVal = dragging ? localVal : progress;
  const snapped = snapToStage(displayVal);
  const currentColor = getProgressColor(snapped);
  const heightPx = size === 'sm' ? 8 : size === 'lg' ? 20 : 12;

  const handleInput = (e) => {
    if (disabled) return;
    const v = parseInt(e.target.value, 10);
    setLocalVal(v);
    setDragging(true);
  };

  const handleChange = (e) => {
    if (disabled) return;
    const v = parseInt(e.target.value, 10);
    const sv = snapToStage(v);
    setLocalVal(sv);
    setDragging(false);
    if (onProgressChange) onProgressChange(sv);
  };

  const handleStageClick = (stageValue) => {
    if (disabled) return;
    setLocalVal(stageValue);
    setDragging(false);
    if (onProgressChange) onProgressChange(stageValue);
  };

  return (
    <div className="w-full" data-testid="progress-slider">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold" style={{ color: currentColor }}>
          {snapped}%
        </span>
      </div>

      {/* Native range input - works reliably on all touch devices */}
      <div className="relative" style={{ height: heightPx + 8 }}>
        {/* Background track */}
        <div
          className="absolute left-0 right-0 rounded-full bg-stone-200"
          style={{ height: heightPx, top: 4 }}
        >
          {/* Filled portion */}
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all duration-100"
            style={{ width: `${displayVal}%`, backgroundColor: currentColor }}
          />
          {/* Stage markers */}
          {PROGRESS_STAGES.slice(1).map(s => (
            <div
              key={s.value}
              className="absolute top-0 bottom-0 w-0.5 bg-white/60"
              style={{ left: `${s.value}%`, transform: 'translateX(-50%)' }}
            />
          ))}
        </div>
        {/* Invisible native range on top */}
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={displayVal}
          onInput={handleInput}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 w-full opacity-0 cursor-pointer z-10"
          style={{ height: heightPx + 8 }}
          data-testid="progress-range-input"
        />
        {/* Visual thumb */}
        {!disabled && (
          <div
            className="absolute rounded-full bg-white border-2 shadow-md pointer-events-none transition-all duration-100"
            style={{
              width: size === 'sm' ? 14 : 18,
              height: size === 'sm' ? 14 : 18,
              left: `${displayVal}%`,
              top: '50%',
              transform: 'translateX(-50%) translateY(-50%)',
              borderColor: currentColor,
            }}
          />
        )}
      </div>

      {/* Stage label buttons */}
      {showLabels && !disabled && (
        <div className="flex justify-between mt-1.5">
          {PROGRESS_STAGES.map((stage) => (
            <button
              key={stage.value}
              type="button"
              onClick={() => handleStageClick(stage.value)}
              disabled={disabled}
              className={`text-[10px] font-medium transition-all hover:scale-110 ${
                snapped >= stage.value ? 'opacity-100' : 'opacity-50 hover:opacity-75'
              }`}
              style={{ color: snapped >= stage.value ? stage.color : '#9CA3AF' }}
              data-testid={`stage-${stage.value}`}
            >
              {stage.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { PROGRESS_STAGES, getProgressColor, getProgressBgClass, snapToStage };
